import { z } from 'zod';
import { characterCodeSchema } from '../creation/code.js';
import { contentIdSchema } from '../rules/content-id.js';
import { resourceIdSchema } from '../content/resource.js';
import { opaqueTokenSchema } from '../auth/schemas.js';
/**
 * Кампании, места, приглашения и заявки (D8).
 *
 * Всё, что пересекает границу процесса, объявлено здесь и типы
 * выводятся отсюда же (CLAUDE.md, п. 6).
 *
 * Вход в кампанию идёт по одной ссылке кампании, живущей до отзыва
 * (D51). Место появляется в момент, когда мастер подтверждает
 * заявку, — заранее мест нет. Одноразовая ссылка на конкретное место
 * (D38) осталась ниже: она работает у кампаний, заведённых раньше,
 * и ждёт отдельной задачи, но из интерфейса убрана.
 *
 * Что видно до подтверждения: по общей ссылке — название кампании
 * и сколько осталось мест (скрывать нечего, название стоит в том же
 * сообщении чата); по одноразовой — по-прежнему ничего (D38).
 * Состава партии и чужих персонажей не видно ни там, ни там.
 */
const uuid = z.uuid();
/**
 * Игровая система кампании (D6).
 *
 * Значение одно, и список закрыт: поле есть, ветвлений по нему в коде
 * нет. Вторая система появится решением, а не строкой, пришедшей
 * от клиента.
 */
export const RULESETS = ['dnd5e-2024'];
export const rulesetSchema = z.enum(RULESETS);
/** Название кампании: то, что мастер напишет от руки. */
export const campaignTitleSchema = z
    .string()
    .trim()
    .min(1, 'у кампании должно быть название')
    .max(120, 'название длиннее 120 знаков не поместится ни на один экран');
/** Подпись места: «лучник», «Мурка», «стул у окна». */
export const seatLabelSchema = z
    .string()
    .trim()
    .min(1, 'у места должна быть подпись')
    .max(120, 'подпись длиннее 120 знаков не поместится ни на один экран');
/**
 * Предел участников кампании (D51).
 *
 * Верхняя граница — не вкус, а защита: заявок в кампанию столько же,
 * сколько мест, и «тысяча мест» означала бы кампанию, куда можно
 * положить тысячу заявок.
 */
export const seatLimitSchema = z
    .number()
    .int()
    .min(1, 'в кампании должно быть хотя бы одно место')
    .max(64, 'больше 64 мест за одним столом не бывает');
export const seatStatusSchema = z.enum(['free', 'pending', 'taken']);
/**
 * Вид заявки в очереди мастера. `edit` — правка выборов персонажа,
 * который уже за столом (D62): та же очередь, что для входа, чтобы
 * мастер смотрел один список.
 */
export const joinRequestKindSchema = z.enum(['first_login', 'device_change', 'edit']);
/** Заявки, которые решают, за каким местом сидит устройство. */
export const seatRequestKindSchema = z.enum(['first_login', 'device_change']);
/* ── Кампания ────────────────────────────────────────────────────── */
/**
 * Стартовый уровень кампании (D60).
 *
 * Уровень, с которого мастер пускает новых за стол. Персонаж при
 * этом всё равно собирается по шагам — создание плюс повышения, —
 * потому что персонаж есть журнал решений (D4), и третий уровень
 * это три шага, а не одна форма.
 */
export const startLevelSchema = z.number().int().min(1).max(20);
export const campaignSchema = z.strictObject({
    id: uuid,
    title: campaignTitleSchema,
    ruleset: rulesetSchema,
    /** Признак «использовать пользовательский контент» (D30). */
    useCustomContent: z.boolean(),
    /** Момент архивации; `null` — кампания живая (D37). */
    archivedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    /** Сколько мест заведено и сколько из них занято — для списка. */
    seats: z.number().int().nonnegative(),
    taken: z.number().int().nonnegative(),
    /** Сколько заявок ждёт решения: это то, ради чего мастер зайдёт. */
    pendingRequests: z.number().int().nonnegative(),
    /** Предел участников (D51): сколько мест мастер готов раздать. */
    seatLimit: seatLimitSchema,
    /** Сколько мест предела уже занято: занятые и ожидающие. */
    seatsUsed: z.number().int().nonnegative(),
    /** Уровень, с которого мастер пускает новых за стол (D60). */
    startLevel: startLevelSchema,
    /**
     * Токен общей ссылки; `null` — ссылки нет или её отозвали.
     *
     * Именно токен, а не готовая ссылка: адрес клиента знает клиент,
     * и собрать её он может сам (`inviteLink`). Токен открыт и мастеру
     * показывается сколько угодно раз — он копирует его каждому
     * опоздавшему (D51).
     */
    inviteToken: z.string().min(1).nullable(),
});
export const createCampaignInputSchema = z.strictObject({
    title: campaignTitleSchema,
    /**
     * Система правил приходит от клиента, а не подставляется сервером:
     * поле принадлежит кампании (D6), и умолчание в двух местах разъедется
     * в тот день, когда систем станет две.
     */
    ruleset: rulesetSchema,
    useCustomContent: z.boolean(),
    /** Предел участников (D51); не задан — шесть, обычный стол. */
    seatLimit: seatLimitSchema.optional(),
    /** Стартовый уровень (D60); не задан — первый. */
    startLevel: startLevelSchema.optional(),
});
/**
 * Правка настроек. Каждое поле необязательно: экран настроек меняет
 * одно, а не переприсылает всё — иначе две открытые вкладки затирают
 * друг друга целиком, а не по полю.
 */
export const updateCampaignInputSchema = z.strictObject({
    campaignId: uuid,
    title: campaignTitleSchema.optional(),
    useCustomContent: z.boolean().optional(),
    /**
     * Предел участников (D51). Понижать ниже числа уже занятых мест
     * нельзя: это выгнало бы тех, кто уже за столом, — сервер откажет
     * с объяснением.
     */
    seatLimit: seatLimitSchema.optional(),
    /**
     * Стартовый уровень (D60). Меняется в любую сторону: он касается
     * тех, кто придёт после, а уже сидящих за столом не трогает —
     * понижать чужой уровень мы не станем (D61).
     */
    startLevel: startLevelSchema.optional(),
});
export const archiveCampaignInputSchema = z.strictObject({
    campaignId: uuid,
    /** `true` — в архив, `false` — обратно в работу (D37). */
    archived: z.boolean(),
});
export const campaignListInputSchema = z.strictObject({
    /** Показать и архивные тоже. По умолчанию их в списке нет (D37). */
    includeArchived: z.boolean(),
});
export const campaignListOutputSchema = z.array(campaignSchema);
export const campaignResultSchema = z.strictObject({ campaign: campaignSchema });
/* ── Место ───────────────────────────────────────────────────────── */
/**
 * Ссылка на запись контента: ключ и имя из источника.
 *
 * Одного имени мало: имя приходит из записи и потому английское
 * («Dwarf», «Fighter»), а перевод интерфейс ищет по ключу (D11, D32).
 * Имя остаётся рядом как запасной вариант — запись без перевода
 * покажется по-английски, но покажется.
 */
export const contentRefSchema = z.strictObject({
    id: contentIdSchema,
    name: z.string().min(1),
});
/** Персонаж на месте — короткой строкой: списку мест хватает этого. */
export const seatCharacterSchema = z.strictObject({
    id: uuid,
    name: z.string().min(1),
    code: characterCodeSchema,
});
export const seatSchema = z.strictObject({
    id: uuid,
    campaignId: uuid,
    label: seatLabelSchema,
    status: seatStatusSchema,
    /**
     * Есть ли живое приглашение и до какого момента. Самого токена
     * здесь нет: он показывается один раз, в ответ на выпуск ссылки,
     * и больше его не восстановить — в базе лежит хеш (D38).
     */
    inviteExpiresAt: z.iso.datetime().nullable(),
    character: seatCharacterSchema.nullable(),
    createdAt: z.iso.datetime(),
});
export const createSeatInputSchema = z.strictObject({
    campaignId: uuid,
    label: seatLabelSchema,
});
export const renameSeatInputSchema = z.strictObject({
    seatId: uuid,
    label: seatLabelSchema,
});
export const releaseSeatInputSchema = z.strictObject({ seatId: uuid });
/**
 * Ответ на освобождение места (D37): место свободно, а персонаж
 * вернулся устройству игрока и снова стал драфтом. Идентификатор
 * вернувшегося приходит, чтобы интерфейс мог сказать, что именно
 * произошло, — «место освобождено» без этого выглядит как удаление.
 */
export const releaseSeatResultSchema = z.strictObject({
    seat: seatSchema,
    returnedCharacterId: uuid.nullable(),
});
export const seatListInputSchema = z.strictObject({ campaignId: uuid });
export const seatListOutputSchema = z.array(seatSchema);
export const seatResultSchema = z.strictObject({ seat: seatSchema });
/* ── Приглашение ─────────────────────────────────────────────────── */
export const issueInviteInputSchema = z.strictObject({ seatId: uuid });
/**
 * Выпущенная ссылка.
 *
 * Показывается один раз: в базе лежит хеш, и восстановить ссылку
 * нельзя — только выпустить новую, погасив прежнюю (D38). Интерфейс
 * обязан дать её скопировать сразу.
 */
export const inviteResultSchema = z.strictObject({
    link: z.string().min(1),
    expiresAt: z.iso.datetime(),
    seat: seatSchema,
});
export const revokeInviteInputSchema = z.strictObject({ seatId: uuid });
/* ── Ссылка кампании (D51) ───────────────────────────────────────── */
export const campaignInviteInputSchema = z.strictObject({ campaignId: uuid });
/**
 * Что игрок видит, перейдя по общей ссылке, до всякой заявки.
 *
 * Название кампании и сколько осталось мест — и ничего больше (D51).
 * Название и так стоит в том же сообщении чата, где ссылка, а без
 * него человек не поймёт, туда ли он попал, прежде чем строить
 * персонажа. Состава партии и чужих персонажей не видно.
 *
 * `useCustomContent` — не украшение: по нему собирается план создания
 * персонажа (D30), и без него визард показал бы не тот набор.
 */
export const invitePreviewSchema = z.strictObject({
    campaignId: uuid,
    title: campaignTitleSchema,
    ruleset: rulesetSchema,
    useCustomContent: z.boolean(),
    seatLimit: seatLimitSchema,
    seatsUsed: z.number().int().nonnegative(),
    /**
     * Уровень, с которого мастер пускает за стол (D60). Пришедший
     * по ссылке видит его до того, как начнёт собирать персонажа:
     * иначе он соберёт первого и узнает про третий из отказа.
     */
    startLevel: startLevelSchema,
});
export const invitePreviewInputSchema = z.strictObject({
    token: z.string().min(1).max(200),
});
/**
 * Откуда персонаж в заявке (D51): создан только что или подобран
 * из своих драфтов. Подсказка мастеру, а не факт — приходит
 * от клиента и ничего не открывает.
 */
export const joinCharacterOriginSchema = z.enum(['new', 'draft']);
/**
 * Заявка по общей ссылке (D51).
 *
 * Персонаж приносится с собой: либо создан только что, либо подобран
 * из своих драфтов. Места в заявке нет — оно появится, когда мастер
 * подтвердит.
 */
/**
 * Кого приносят в заявку (D55).
 *
 * Либо своего персонажа по идентификатору, либо своего же, но
 * с другого устройства, — по коду. Разница в схеме, а не в подписи
 * к полю: ветка «по коду» ищет персонажа без проверки владельца,
 * и перепутать её с первой значило бы пустить за стол не того.
 */
export const joinCharacterRefSchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('mine'), characterId: uuid }),
    z.strictObject({ kind: z.literal('code'), code: characterCodeSchema }),
]);
export const requestJoinInputSchema = z.strictObject({
    token: z.string().min(1).max(200),
    character: joinCharacterRefSchema,
    origin: joinCharacterOriginSchema,
});
/** Почему заявка отклонена сама, без мастера (D51). */
export const joinRejectReasonSchema = z.enum(['seat_limit']);
/**
 * Ответ на заявку по ссылке.
 *
 * Отказ по пределу мест приходит сразу и с причиной: заявка при этом
 * записана и видна мастеру — он поднимет предел, и игрок перейдёт
 * по той же ссылке снова (D51). Молчаливый отказ выглядел бы
 * поломкой, а человек мог перейти по чужой ссылке без злого умысла.
 */
export const requestJoinResultSchema = z.strictObject({
    requestId: uuid,
    status: z.enum(['pending', 'rejected']),
    reason: joinRejectReasonSchema.nullable(),
});
/* ── Заявки ──────────────────────────────────────────────────────── */
/**
 * Персонаж в заявке — с подробностями (D51).
 *
 * Раз ссылка публична, подтверждение не должно быть кнопкой, которую
 * жмут не глядя: имя, вид, класс и уровень — это то, по чему мастер
 * отличает своего от случайного. Вид и класс ссылками на записи,
 * а не одним именем: перевод интерфейс ищет по ключу (D11, D32).
 */
export const joinCharacterSchema = z.strictObject({
    id: uuid,
    name: z.string().min(1),
    code: characterCodeSchema,
    /** Вид и класс; `null` — выбора в журнале нет. */
    species: contentRefSchema.nullable(),
    characterClass: contentRefSchema.nullable(),
    level: z.number().int().positive(),
});
/**
 * Несоответствие персонажа кампании (D61).
 *
 * Их бывает несколько сразу, и разбираются они в одном месте —
 * при подтверждении заявки, — а не отказами по одному в разных
 * углах приложения. Мастер видит список и решает по каждому пункту;
 * молчаливых решений нет.
 *
 * Понижения уровня среди выходов нет и не будет: снять уровни значит
 * отменить чужие выборы. Мастер принимает как есть, возвращает
 * на переделку или поднимает уровень кампании.
 */
export const joinConflictSchema = z.discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('level'),
        /** Уровень персонажа и уровень, с которого кампания стартует. */
        characterLevel: z.number().int().positive(),
        campaignLevel: startLevelSchema,
    }),
    z.strictObject({
        /** Записи, на которые персонаж ссылается, а найти их негде. */
        kind: z.literal('missing-content'),
        refs: z.array(contentRefSchema).min(1),
    }),
    z.strictObject({
        /** Свои записи игрока: кампания их ещё не подтверждала (D62). */
        kind: z.literal('unapproved-content'),
        refs: z.array(contentRefSchema).min(1),
    }),
    z.strictObject({
        /** Персонаж опирается на свой контент, а в кампании он выключен. */
        kind: z.literal('custom-disabled'),
        refs: z.array(contentRefSchema).min(1),
    }),
]);
/**
 * Одна строка правки (D62): слот, что в нём было и что станет.
 *
 * С названиями, а не одними ключами: мастер решает по тому, что
 * читает, а `srd:guidance` ему ничего не говорит. Списком, потому
 * что слот бывает и на несколько значений — заговоры, навыки.
 */
/**
 * Значение в строке правки: что выбрано и как оно зовётся.
 *
 * И то и другое: SRD клиент переводит по ключу, а запись игрока
 * переводить нечем — её имя приходит как есть.
 */
export const editValueSchema = z.strictObject({
    value: z.string().min(1),
    name: z.string(),
});
export const editChangeSchema = z.strictObject({
    slot: z.string().min(1),
    before: z.array(editValueSchema),
    after: z.array(editValueSchema),
});
export const joinRequestSchema = z.strictObject({
    id: uuid,
    campaignId: uuid,
    /**
     * Место, за которым пришли; `null` — заявка по общей ссылке (D51):
     * места ещё нет, оно появится при подтверждении.
     */
    seatId: uuid.nullable(),
    seatLabel: seatLabelSchema.nullable(),
    kind: joinRequestKindSchema,
    /** `pending` — ждёт мастера; `rejected` — отклонена сама (D51). */
    status: z.enum(['pending', 'rejected']),
    /** Причина отказа без мастера; `null` — заявка ждёт решения. */
    reason: joinRejectReasonSchema.nullable(),
    /** Откуда персонаж; `null` — заявка на смену устройства. */
    origin: joinCharacterOriginSchema.nullable(),
    createdAt: z.iso.datetime(),
    /**
     * Персонаж, за которым пришли. У заявки по ссылке — принесённый
     * с собой, у смены устройства — тот, что уже сидит за местом.
     */
    character: joinCharacterSchema.nullable(),
    /**
     * Что у персонажа не сходится с кампанией (D61). Пустой список —
     * сходится всё.
     */
    conflicts: z.array(joinConflictSchema),
    /**
     * Что меняет правка (D62): по строке на слот, было и стало,
     * названиями. `null` — заявка не на правку.
     */
    changes: z.array(editChangeSchema).nullable(),
});
export const joinRequestListInputSchema = z.strictObject({ campaignId: uuid });
export const joinRequestListOutputSchema = z.array(joinRequestSchema);
export const resolveJoinRequestInputSchema = z.strictObject({
    requestId: uuid,
    /** `true` — пустить, `false` — отклонить. */
    approved: z.boolean(),
    /**
     * Что сказать игроку при отказе (D61).
     *
     * Отказ без объяснения оставляет человека гадать, что чинить:
     * уровень, запись, которой нет, или вовсе ничего — мастеру просто
     * не понравилось. Поле необязательное: молчаливый отказ остаётся
     * возможным, но он хотя бы выбран, а не единственный.
     */
    note: z.string().max(500).optional(),
});
/**
 * Ответ на решение мастера.
 *
 * Место приходит только у подтверждения: у отказа места нет и быть
 * не может — оно и создаётся подтверждением (D51).
 */
export const resolveJoinRequestResultSchema = z.strictObject({
    seat: seatSchema.nullable(),
});
/* ── Сторона игрока ──────────────────────────────────────────────── */
export const claimInviteInputSchema = z.strictObject({
    token: opaqueTokenSchema,
});
/**
 * Ответ на переход по ссылке.
 *
 * Ни названия кампании, ни подписи места, ни числа игроков — ничего
 * (D38). Только «заявка принята», и это максимум, который можно
 * сказать, не спросив мастера: ссылку могли переслать не тому.
 */
export const claimInviteResultSchema = z.strictObject({
    requestId: uuid,
    kind: joinRequestKindSchema,
});
/**
 * Заявка на возврат за своё место с нового устройства (D38).
 *
 * По коду персонажа, а не по ссылке: ссылка одноразовая и уже
 * погашена, а второй способ попасть в кампанию пришлось бы защищать
 * отдельно.
 */
export const requestDeviceChangeInputSchema = z.strictObject({
    code: characterCodeSchema,
});
/**
 * Что игрок видит про своё место.
 *
 * Пока заявка не решена, `status` — `pending`, а кампании и места
 * в ответе нет вовсе. После подтверждения появляются название
 * кампании и подпись места: с этого момента игрок в партии.
 */
export const seatStateSchema = z.discriminatedUnion('status', [
    z.strictObject({
        status: z.literal('pending'),
        requestId: uuid,
        // Правка (D62) места не меняет и сюда не попадает: игрок
        // за столом остаётся за столом, пока мастер решает.
        kind: seatRequestKindSchema,
    }),
    z.strictObject({
        status: z.literal('rejected'),
        /**
         * Что сказал мастер (D61); `null` — отказал молча.
         *
         * Игрок видит это там же, где подавал заявку: отказ без причины
         * оставляет его гадать, что чинить.
         */
        note: z.string().nullable(),
    }),
    z.strictObject({
        status: z.literal('taken'),
        seatId: uuid,
        seatLabel: seatLabelSchema,
        campaignId: uuid,
        campaignTitle: campaignTitleSchema,
        ruleset: rulesetSchema,
        useCustomContent: z.boolean(),
        /** Стартовый уровень кампании (D60): умолчание сборки персонажа. */
        startLevel: startLevelSchema,
        archived: z.boolean(),
        character: seatCharacterSchema.nullable(),
    }),
]);
/** Места этого устройства: игрок мог сесть за стол не в одной партии. */
export const mySeatsOutputSchema = z.array(seatStateSchema);
/**
 * Подбор своего драфта за место по коду (D29).
 *
 * Персонаж въезжает в кампанию вместе со своим журналом: это тот же
 * персонаж, а не копия. Код, а не идентификатор, потому что код
 * диктуют голосом за столом.
 */
export const attachCharacterInputSchema = z.strictObject({
    seatId: uuid,
    code: characterCodeSchema,
});
/* ── События кампании (D2) ───────────────────────────────────────── */
/**
 * Что произошло за столом.
 *
 * Событие — не уведомление, а факт: у него есть номер в рамках
 * кампании, и по нему клиент понимает, всё ли он видел. Отсюда
 * и требования к списку: он закрыт, и новый вид события — это
 * решение, а не строка, пришедшая от кого-то.
 *
 * Чего в событиях нет и не будет: токена устройства, адреса почты
 * и ссылки-приглашения. Событие видят все участники кампании,
 * а перечисленное — это доступ (D38, security-checklist.md, п. 3).
 */
export const CAMPAIGN_EVENT_TYPES = [
    /** Кто-то попросился за место: перешёл по ссылке или назвал код. */
    'join_request.created',
    /** Мастер решил: пустил или отказал. */
    'join_request.resolved',
    /** Место освобождено мастером; персонаж вернулся игроку (D37). */
    'seat.released',
    /** Команда мастера над хитами: урон, лечение, временные (D3). */
    'character.hp',
    /** Мастер выдал предмет или забрал его. */
    'character.inventory',
    /** Мастер повесил состояние или снял его. */
    'character.condition',
    /** Мастер начислил опыт; у драфта — начисление себе. */
    'character.xp',
    /** Мастер потратил или вернул счётчик ресурса на чужом листе (D56). */
    'character.resource',
    /** Отдых объявлен на всю партию. */
    'rest.declared',
    /** Бросок за столом: виден всем, у скрытого нет результата (D12). */
    'roll.made',
    /** Бой начался, очередь собрана. */
    'initiative.started',
    /** Очередь изменилась: строку добавили, убрали или бросили за неё. */
    'initiative.changed',
    /** Ход перешёл дальше; круг мог смениться. */
    'initiative.turn',
    /** Бой окончен, очередь снята. */
    'initiative.ended',
];
export const campaignEventTypeSchema = z.enum(CAMPAIGN_EVENT_TYPES);
/**
 * Поля, общие для всех событий: номер, вид и время.
 *
 * Номер монотонен в рамках кампании и не имеет дыр (D2): по нему
 * клиент догоняет пропущенное и по нему же отличает «ничего
 * не происходило» от «я отвалился».
 */
const eventBase = {
    seq: z.number().int().positive(),
    at: z.iso.datetime(),
};
/**
 * События места: кто и куда сел. Подпись места идёт вместе
 * с идентификатором — лента читается за столом вслух, и «место
 * 4f2c…» в ней не читается никак.
 */
/**
 * События про место.
 *
 * Место может быть неизвестно: заявка по общей ссылке приходит,
 * когда места ещё нет, — оно появится при подтверждении (D51).
 * Тогда пусты оба поля, и это не «потерялось», а «ещё не создано».
 */
const seatEvent = {
    ...eventBase,
    seatId: uuid.nullable(),
    seatLabel: seatLabelSchema.nullable(),
};
/**
 * События персонажа: имя рядом с идентификатором по той же причине.
 * Имени места здесь нет — за столом персонажа зовут по имени.
 */
const characterEvent = {
    ...eventBase,
    characterId: uuid,
    characterName: z.string().min(1),
};
export const campaignEventSchema = z.discriminatedUnion('type', [
    z.strictObject({
        ...seatEvent,
        type: z.literal('join_request.created'),
        requestKind: joinRequestKindSchema,
    }),
    z.strictObject({
        ...seatEvent,
        type: z.literal('join_request.resolved'),
        approved: z.boolean(),
    }),
    z.strictObject({
        ...seatEvent,
        type: z.literal('seat.released'),
    }),
    z.strictObject({
        ...characterEvent,
        type: z.literal('character.hp'),
        hpKind: z.enum(['damage', 'heal', 'temp']),
        amount: z.number().int().nonnegative(),
        /** Куда пришли хиты после команды: лента показывает итог, а не дельту. */
        current: z.number().int().nonnegative(),
        max: z.number().int().nonnegative(),
    }),
    z.strictObject({
        ...characterEvent,
        type: z.literal('character.inventory'),
        item: contentIdSchema,
        itemName: z.string().min(1),
        quantity: z.number().int().positive(),
        /** `true` — забрали, `false` — выдали. */
        taken: z.boolean(),
    }),
    z.strictObject({
        ...characterEvent,
        type: z.literal('character.condition'),
        condition: contentIdSchema,
        conditionName: z.string().min(1),
        /** Уровень накопительного состояния; `null` — уровней нет. */
        level: z.number().int().positive().nullable(),
        /** `true` — повесили, `false` — сняли. */
        applied: z.boolean(),
    }),
    z.strictObject({
        ...characterEvent,
        type: z.literal('character.xp'),
        amount: z.number().int(),
        total: z.number().int().nonnegative(),
        /** Порог взят и повышение стало доступно (D31). */
        levelUpAvailable: z.boolean(),
    }),
    z.strictObject({
        ...characterEvent,
        type: z.literal('character.resource'),
        resource: resourceIdSchema,
        resourceName: z.string().min(1),
        /** Отрицательное — вернули, положительное — потратили. */
        amount: z.number().int(),
        /** Куда пришёл счётчик: за столом важен остаток, а не «минус один». */
        current: z.number().int().nonnegative(),
        max: z.number().int().nonnegative(),
    }),
    z.strictObject({
        ...eventBase,
        type: z.literal('rest.declared'),
        restKind: z.enum(['short', 'long']),
        applied: z.number().int().nonnegative(),
    }),
    z.strictObject({
        ...eventBase,
        type: z.literal('roll.made'),
        who: z.string().min(1),
        characterId: uuid.nullable(),
        rollKind: z.string().min(1),
        /**
         * Итог броска; `null` — бросок скрыт. Отметка о броске в ленте
         * остаётся: за столом видно, что мастер что-то бросил, но не что
         * именно выпало.
         */
        total: z.number().int().nullable(),
        hidden: z.boolean(),
    }),
    z.strictObject({
        ...eventBase,
        type: z.literal('initiative.started'),
        round: z.number().int().positive(),
    }),
    z.strictObject({
        ...eventBase,
        type: z.literal('initiative.changed'),
    }),
    z.strictObject({
        ...eventBase,
        type: z.literal('initiative.turn'),
        round: z.number().int().positive(),
        turn: z.number().int().nonnegative(),
        /** Чей ход стал: подпись строки, чтобы лента читалась вслух. */
        who: z.string().min(1),
    }),
    z.strictObject({
        ...eventBase,
        type: z.literal('initiative.ended'),
    }),
]);
/**
 * Кадр потока событий.
 *
 * Три вида, и все три нужны. `event` — само событие. `ready` —
 * первый кадр после подключения: клиент узнаёт, на каком номере
 * сейчас кампания, и отличает «подключились, тихо» от «подключение
 * висит». `resync` — «отстал слишком сильно, перечитай состояние
 * целиком»: досылать сотни событий дороже и для сервера, и для
 * клиента, чем один полный запрос (architecture.md, раздел 4).
 */
export const campaignStreamFrameSchema = z.discriminatedUnion('frame', [
    z.strictObject({
        frame: z.literal('ready'),
        lastSeq: z.number().int().nonnegative(),
    }),
    z.strictObject({ frame: z.literal('event'), event: campaignEventSchema }),
    z.strictObject({
        frame: z.literal('resync'),
        lastSeq: z.number().int().nonnegative(),
    }),
]);
/**
 * Путь потока событий кампании.
 *
 * Не процедура tRPC: tRPC отвечает одним ответом, а здесь поток,
 * живущий часами (D2). Путь объявлен здесь по той же причине, что
 * и пути из писем, — его собирает клиент, а обслуживает сервер,
 * и это граница процесса (CLAUDE.md, п. 6).
 */
export const CAMPAIGN_EVENTS_PATH = '/events/campaign';
/** Адрес потока: кампания в пути, известный клиенту номер — в запросе. */
export function campaignEventsPath(campaignId, after) {
    return `${CAMPAIGN_EVENTS_PATH}/${encodeURIComponent(campaignId)}?after=${after}`;
}
/**
 * Сколько пропущенных событий сервер досылает, а сколько уже нет.
 *
 * Число выбрано не расчётом, а здравым смыслом: сотня событий —
 * это несколько минут активной игры, и досылать их дешевле, чем
 * перечитывать состояние. Тысяча — это перерыв на обед, и там уже
 * дешевле перечитать.
 */
export const EVENT_BACKLOG_LIMIT = 100;
//# sourceMappingURL=schemas.js.map